import { useCallback, useEffect, useRef } from 'react';
import { auth } from '../../firebase-client';
import { useAuth } from '../../auth/AuthContext';
import { reportCaughtError } from '../../utils/clientErrorReporting';
import { isWebKitBrowser } from '../../utils/firestoreOffline';
import { isOnline, useOnlineStatus } from '../../utils/onlineStatus';
import {
  FILE_PICKER_CLOSE_EVENT,
  FILE_PICKER_OPEN_EVENT,
} from '../../utils/filePickerActivity';
import { usePwaInstallPrompt } from './PwaInstallContext';
import './pwa.css';

const INITIAL_EVENT_GRACE_MS = 2000;
const AUTO_REFRESH_MS = 3000;
const AUTO_REFRESH_THROTTLE_MS = 2 * 60 * 1000;
const AUTO_REFRESH_SESSION_KEY = 'memberNest.lastResumeAutoRefreshAt';
const RESUME_TASK_TIMEOUT_MS = 2500;
const RESUME_TIMEOUT_REPORT_THROTTLE_MS = 5 * 60 * 1000;
const RESUME_TIMEOUT_REPORT_SESSION_KEY = 'memberNest.lastResumeTimeoutReportAt';
const FILE_PICKER_MAX_OPEN_MS = 5 * 60 * 1000;
const FILE_PICKER_CLOSE_GRACE_MS = 1500;

function currentRoute() {
  if (typeof window === 'undefined') return '';
  return `${window.location.pathname}${window.location.search || ''}`;
}

async function refreshServiceWorkerRegistration() {
  if (typeof navigator === 'undefined' || !navigator.serviceWorker?.getRegistration) return;
  const registration = await navigator.serviceWorker.getRegistration();
  if (typeof registration?.update === 'function') {
    await registration.update();
  }
}

async function refreshAuthToken() {
  const currentUser = auth.currentUser;
  if (typeof currentUser?.getIdToken === 'function') {
    // Firebase refreshes an expired token automatically. Forcing a refresh on
    // every mobile-PWA resume creates an unnecessary network request and can
    // exceed the bounded resume timeout on otherwise healthy connections.
    await currentUser.getIdToken();
  }
}

function withTimeout(promise, timeoutMs = RESUME_TASK_TIMEOUT_MS) {
  let timer;
  return Promise.race([
    promise,
    new Promise((resolve) => {
      timer = setTimeout(() => resolve({ timedOut: true }), timeoutMs);
    }),
  ]).finally(() => clearTimeout(timer));
}

async function runBoundedResumeTask(name, task) {
  try {
    const result = await withTimeout(Promise.resolve().then(task));
    return { name, timedOut: Boolean(result?.timedOut) };
  } catch (err) {
    // Resume is best-effort; the refresh signal below lets hooks retry their own reads.
    return { name, error: err?.code || err?.name || 'resume_task_failed' };
  }
}

async function reconnectFirebase() {
  refreshServiceWorkerRegistration().catch(() => {});
  // Firestore reconnects its active watch stream automatically. Do not call
  // enableNetwork here: that API pairs with an explicit disableNetwork call,
  // and restarting a live stream can resend an existing listen target.
  const results = await Promise.allSettled([
    runBoundedResumeTask('auth_token_refresh', refreshAuthToken),
  ]);
  return results.map((result) => (result.status === 'fulfilled'
    ? result.value
    : { name: 'resume_task', error: result.reason?.code || result.reason?.name || 'resume_task_failed' }));
}

function dispatchOnlineRefresh() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event('membernest:online-refresh'));
}

function readLastAutoRefreshAt() {
  try {
    return Number(window.sessionStorage.getItem(AUTO_REFRESH_SESSION_KEY) || 0);
  } catch (err) {
    return 0;
  }
}

function writeLastAutoRefreshAt(value) {
  try {
    window.sessionStorage.setItem(AUTO_REFRESH_SESSION_KEY, String(value));
  } catch (err) {}
}

function readLastTimeoutReportAt() {
  try {
    return Number(window.sessionStorage.getItem(RESUME_TIMEOUT_REPORT_SESSION_KEY) || 0);
  } catch (err) {
    return 0;
  }
}

function writeLastTimeoutReportAt(value) {
  try {
    window.sessionStorage.setItem(RESUME_TIMEOUT_REPORT_SESSION_KEY, String(value));
  } catch (err) {}
}

function shouldReportNonWebKitResumeTimeout() {
  if (typeof window === 'undefined') return false;
  if (isWebKitBrowser(window)) return false;
  const now = Date.now();
  const lastReportAt = readLastTimeoutReportAt();
  if (now - lastReportAt < RESUME_TIMEOUT_REPORT_THROTTLE_MS) return false;
  writeLastTimeoutReportAt(now);
  return true;
}

export default function AppResumeMonitor() {
  const { user, authLoading } = useAuth();
  const { isStandalone } = usePwaInstallPrompt();
  const online = useOnlineStatus();
  const mountedAtRef = useRef(Date.now());
  const authLoadingRef = useRef(authLoading);
  const autoRefreshTimerRef = useRef(null);
  const inFlightRef = useRef(false);
  const filePickerActiveRef = useRef(false);
  const ignoreResumeUntilRef = useRef(0);

  useEffect(() => {
    authLoadingRef.current = authLoading;
  }, [authLoading]);

  const clearTimers = useCallback(() => {
    window.clearTimeout(autoRefreshTimerRef.current);
  }, []);

  const maybeAutoRefresh = useCallback((reason) => {
    const shouldRefresh = authLoadingRef.current || inFlightRef.current;
    if (!shouldRefresh) return;

    const now = Date.now();
    const lastRefreshAt = readLastAutoRefreshAt();
    const throttled = now - lastRefreshAt < AUTO_REFRESH_THROTTLE_MS;

    reportCaughtError(new Error(throttled
      ? 'App resume is still reconnecting after auto-refresh throttle'
      : 'App resume auto-refresh triggered'), {
      source: throttled ? 'app.resume.autoRefresh.throttled' : 'app.resume.autoRefresh',
      severity: 'warning',
      context: {
        reason,
        route: currentRoute(),
        authLoading: authLoadingRef.current,
        reconnectInFlight: inFlightRef.current,
        hasUser: Boolean(auth.currentUser?.uid || user?.uid),
        standalone: isStandalone,
        online,
        visibilityState: typeof document === 'undefined' ? '' : document.visibilityState,
      },
    });

    if (throttled) return;
    writeLastAutoRefreshAt(now);
    window.location.reload();
  }, [isStandalone, online, user?.uid]);

  const handleResume = useCallback((reason, { force = false } = {}) => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    if (!force && filePickerActiveRef.current && (reason === 'focus' || reason === 'visibilitychange')) {
      filePickerActiveRef.current = false;
      ignoreResumeUntilRef.current = Date.now() + FILE_PICKER_CLOSE_GRACE_MS;
      return;
    }
    if (!force && Date.now() < ignoreResumeUntilRef.current) return;
    if (!force && Date.now() - mountedAtRef.current < INITIAL_EVENT_GRACE_MS) return;
    const currentlyOnline = force ? isOnline() : online;
    if (!currentlyOnline) return;
    if (!user?.uid && !auth.currentUser?.uid) return;

    clearTimers();
    autoRefreshTimerRef.current = window.setTimeout(() => {
      maybeAutoRefresh(reason);
    }, AUTO_REFRESH_MS);

    if (inFlightRef.current) return;
    inFlightRef.current = true;
    reconnectFirebase()
      .then((taskResults = []) => {
        const timedOutTasks = taskResults
          .filter((result) => result?.timedOut)
          .map((result) => result.name)
          .filter(Boolean);
        if (
          isStandalone
          && authLoadingRef.current
          && timedOutTasks.length > 0
          && shouldReportNonWebKitResumeTimeout()
        ) {
          reportCaughtError(new Error('App resume reconnect task timed out'), {
            source: 'app.resume.reconnect.slow',
            severity: 'warning',
            context: {
              reason,
              route: currentRoute(),
              timedOutTaskNames: timedOutTasks.join(','),
              authLoading: authLoadingRef.current,
              hasUser: Boolean(auth.currentUser?.uid || user?.uid),
              standalone: isStandalone,
              online,
              visibilityState: typeof document === 'undefined' ? '' : document.visibilityState,
            },
          });
        }
      })
      .finally(() => {
        dispatchOnlineRefresh();
        inFlightRef.current = false;
        if (!authLoadingRef.current) {
          window.clearTimeout(autoRefreshTimerRef.current);
        }
      });
  }, [clearTimers, isStandalone, maybeAutoRefresh, online, user?.uid]);

  useEffect(() => {
    const handleFilePickerOpen = () => {
      clearTimers();
      filePickerActiveRef.current = true;
      ignoreResumeUntilRef.current = Date.now() + FILE_PICKER_MAX_OPEN_MS;
    };
    const handleFilePickerClose = () => {
      filePickerActiveRef.current = false;
      ignoreResumeUntilRef.current = Date.now() + FILE_PICKER_CLOSE_GRACE_MS;
    };
    const handleFocus = () => handleResume('focus');
    const handleOnline = () => handleResume('online', { force: true });
    const handlePageShow = (event) => {
      if (event.persisted) handleResume('pageshow', { force: true });
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') handleResume('visibilitychange');
    };

    window.addEventListener(FILE_PICKER_OPEN_EVENT, handleFilePickerOpen);
    window.addEventListener(FILE_PICKER_CLOSE_EVENT, handleFilePickerClose);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('online', handleOnline);
    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener(FILE_PICKER_OPEN_EVENT, handleFilePickerOpen);
      window.removeEventListener(FILE_PICKER_CLOSE_EVENT, handleFilePickerClose);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearTimers();
    };
  }, [clearTimers, handleResume]);

  return null;
}
